import React, { useState, useRef, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Filter,
  Download,
  LayoutGrid,
  X,
  Check,
} from "lucide-react";
import "./data-table.scss";

// Density options
const DENSITY_OPTIONS = {
  compact: { label: "Compact", padding: "8px 12px" },
  standard: { label: "Standard", padding: "14px 18px" },
  comfortable: { label: "Comfortable", padding: "20px 24px" },
};

export default function DataTable({
  data = [],
  columns = [],
  enableSearch = true,
  enableFilters = true,
  enableColumnVisibility = true,
  enableSorting = true,
  enablePagination = true,
  enableExport = true,
  enableDensity = true,
  enableColumnResizing = true,
  enableRowSelection = false,
  onRowClick,
  pageSize = 10,
  pageSizeOptions = [5, 10, 20, 50, 100],
  emptyMessage = "No data available",
  loading = false,
  className = "",
  initialColumnVisibility = {},
}) {
  // State
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(
    initialColumnVisibility
  );
  const [rowSelection, setRowSelection] = useState({});
  const [density, setDensity] = useState("standard");
  const [columnSizing, setColumnSizing] = useState({});

  // Dropdown states
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showDensityDropdown, setShowDensityDropdown] = useState(false);

  // Refs for click outside
  const columnsRef = useRef(null);
  const filtersRef = useRef(null);
  const densityRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target)) {
        setShowColumnsDropdown(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFiltersDropdown(false);
      }
      if (densityRef.current && !densityRef.current.contains(event.target)) {
        setShowDensityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      rowSelection,
      columnSizing,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  // Export to CSV
  const handleExport = () => {
    const visibleColumns = table.getVisibleLeafColumns();
    const headers = visibleColumns.map((col) => col.columnDef.header || col.id);
    const rows = table.getFilteredRowModel().rows.map((row) =>
      visibleColumns.map((col) => {
        const value = row.getValue(col.id);
        // Handle special cases
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      })
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (!column.getCanSort()) return null;
    const sorted = column.getIsSorted();
    if (sorted === "asc")
      return <ChevronUp className="dt-sort-icon dt-sort-icon--active" />;
    if (sorted === "desc")
      return <ChevronDown className="dt-sort-icon dt-sort-icon--active" />;
    return <ChevronsUpDown className="dt-sort-icon" />;
  };

  // Visible columns count
  const visibleColumnsCount = table.getVisibleLeafColumns().length;
  const totalColumnsCount = table.getAllLeafColumns().length;

  // Check if table is currently being resized
  const isResizing = table.getState().columnSizingInfo.isResizingColumn;

  return (
    <div className={`dt-container ${className}`}>
      {/* Toolbar */}
      <div className="dt-toolbar">
        {/* Search */}
        {enableSearch && (
          <div className="dt-search">
            <Search className="dt-search__icon" size={16} />
            <input
              type="text"
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search all columns..."
              className="dt-search__input"
            />
            {globalFilter && (
              <button
                className="dt-search__clear"
                onClick={() => setGlobalFilter("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Toolbar Actions */}
        <div className="dt-toolbar__actions">
          {/* Columns Toggle */}
          {enableColumnVisibility && (
            <div className="dt-dropdown" ref={columnsRef}>
              <button
                className="dt-toolbar-btn"
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
              >
                <Columns3 size={16} />
                <span>Columns</span>
                <span className="dt-toolbar-btn__badge">
                  {visibleColumnsCount}/{totalColumnsCount}
                </span>
              </button>
              {showColumnsDropdown && (
                <div className="dt-dropdown__menu dt-dropdown__menu--columns">
                  <div className="dt-dropdown__header">
                    <span>Toggle Columns</span>
                    <button
                      className="dt-dropdown__reset"
                      onClick={() => table.toggleAllColumnsVisible(true)}
                    >
                      Show All
                    </button>
                  </div>
                  <div className="dt-dropdown__list">
                    {table.getAllLeafColumns().map((column) => (
                      <label key={column.id} className="dt-dropdown__item">
                        <input
                          type="checkbox"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                        />
                        <span className="dt-dropdown__checkbox">
                          {column.getIsVisible() && <Check size={12} />}
                        </span>
                        <span>{column.columnDef.header || column.id}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          {enableFilters && (
            <div className="dt-dropdown" ref={filtersRef}>
              <button
                className="dt-toolbar-btn"
                onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
              >
                <Filter size={16} />
                <span>Filters</span>
                {columnFilters.length > 0 && (
                  <span className="dt-toolbar-btn__badge dt-toolbar-btn__badge--active">
                    {columnFilters.length}
                  </span>
                )}
              </button>
              {showFiltersDropdown && (
                <div className="dt-dropdown__menu dt-dropdown__menu--filters">
                  <div className="dt-dropdown__header">
                    <span>Column Filters</span>
                    {columnFilters.length > 0 && (
                      <button
                        className="dt-dropdown__reset"
                        onClick={() => setColumnFilters([])}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="dt-dropdown__list">
                    {table
                      .getAllLeafColumns()
                      .filter((column) => column.getCanFilter())
                      .map((column) => {
                        const filterOptions = column.columnDef.filterOptions;
                        const filterValue = column.getFilterValue() ?? "";
                        
                        return (
                          <div key={column.id} className="dt-filter-item">
                            <label>{column.columnDef.header || column.id}</label>
                            {filterOptions && Array.isArray(filterOptions) ? (
                              <select
                                value={filterValue}
                                onChange={(e) =>
                                  column.setFilterValue(e.target.value || undefined)
                                }
                                className="dt-filter-select"
                              >
                                <option value="">All</option>
                                {filterOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={filterValue}
                                onChange={(e) =>
                                  column.setFilterValue(e.target.value)
                                }
                                placeholder={`Filter ${
                                  column.columnDef.header || column.id
                                }...`}
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Density */}
          {enableDensity && (
            <div className="dt-dropdown" ref={densityRef}>
              <button
                className="dt-toolbar-btn"
                onClick={() => setShowDensityDropdown(!showDensityDropdown)}
              >
                <LayoutGrid size={16} />
                <span>Density</span>
              </button>
              {showDensityDropdown && (
                <div className="dt-dropdown__menu">
                  <div className="dt-dropdown__list">
                    {Object.entries(DENSITY_OPTIONS).map(([key, { label }]) => (
                      <button
                        key={key}
                        className={`dt-dropdown__item dt-dropdown__item--btn ${
                          density === key ? "is-active" : ""
                        }`}
                        onClick={() => {
                          setDensity(key);
                          setShowDensityDropdown(false);
                        }}
                      >
                        {density === key && <Check size={14} />}
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export */}
          {enableExport && (
            <button className="dt-toolbar-btn" onClick={handleExport}>
              <Download size={16} />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Table Wrapper */}
      <div className="dt-table-wrapper">
        <table
          className={`dt-table dt-table--${density} ${
            isResizing ? "dt-table--resizing" : ""
          }`}
          style={{
            width: table.getCenterTotalSize(),
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${
                      header.column.getCanSort() ? "dt-th--sortable" : ""
                    } ${enableColumnResizing ? "dt-th--resizable" : ""}`}
                    style={{
                      width: header.getSize(),
                      position: "relative",
                    }}
                  >
                    <div
                      className="dt-th__content"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {enableSorting && getSortIcon(header.column)}
                    </div>
                    {/* Resize Handle */}
                    {enableColumnResizing && (
                      <div
                        className={`dt-resize-handle ${
                          header.column.getIsResizing()
                            ? "dt-resize-handle--active"
                            : ""
                        }`}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumnsCount} className="dt-loading">
                  <div className="dt-loading__spinner"></div>
                  <span>Loading...</span>
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnsCount} className="dt-empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`dt-row ${onRowClick ? "dt-row--clickable" : ""} ${
                    row.getIsSelected() ? "dt-row--selected" : ""
                  }`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      data-label={cell.column.columnDef.header}
                      style={{
                        width: cell.column.getSize(),
                        padding: DENSITY_OPTIONS[density].padding,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="dt-pagination">
          <div className="dt-pagination__info">
            <span className="dt-pagination__showing">
              Showing{" "}
              <strong>
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}
              </strong>{" "}
              of <strong>{table.getFilteredRowModel().rows.length}</strong>{" "}
              results
            </span>
          </div>

          <div className="dt-pagination__controls">
            {/* Page Size Selector */}
            <div className="dt-pagination__size">
              <span>Rows:</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Navigation */}
            <div className="dt-pagination__nav">
              <button
                className="dt-pagination__btn"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                aria-label="First page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                className="dt-pagination__btn"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              <span className="dt-pagination__pages">
                Page{" "}
                <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
                <strong>{table.getPageCount()}</strong>
              </span>

              <button
                className="dt-pagination__btn"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
              <button
                className="dt-pagination__btn"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                aria-label="Last page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
